import { HD_URL_L } from '../constants';
import { generateBreadcrumbList } from '../utils/generate-breadcrumb-list';
import { sanitizeUrlSegment } from '../utils/sanitize-url-segment';

/**
 * Generates structured breadcrumbs for the page per store pages.
 * @param {Object} params - Parameters for generating structured breadcrumbs.
 * @param {Object} params.storeInfo - The store information object.
 * @param {string|number} params.storeInfo.storeId - The unique ID of the store.
 * @param {string} params.storeInfo.name - The name of the store.
 * @param {Object} params.storeInfo.address - The address object for the store.
 * @param {string} params.storeInfo.address.city - The city where the store is located.
 * @param {string} params.storeInfo.address.state - The state where the store is located.
 * @param {string} params.storeInfo.address.postalCode - The postal code of the store.
 * @param {string} [params.departmentName] - The name of the department (optional).
 * @param {string|number} [params.departmentID] - The ID of the department (optional).
 * @returns {Object} The structured breadcrumbs object for the store detail page, formatted for JSON-LD.
 */
export const buildPagePerStoreBreadcrumbs = ({
  storeInfo: {
    storeId,
    name: storeName,
    address: {
      city = '',
      state = '',
      postalCode = ''
    } = {}
  } = {},
  departmentName,
  departmentID
}) => {
  const sanitizedStoreId = sanitizeUrlSegment(storeId);
  const sanitizedName = sanitizeUrlSegment(storeName);
  const sanitizedCity = sanitizeUrlSegment(city);
  const sanitizedState = sanitizeUrlSegment(state);
  const sanitizedPostalCode = sanitizeUrlSegment(postalCode);

  const storeUrlParts = [sanitizedName, sanitizedState, sanitizedCity, sanitizedPostalCode, sanitizedStoreId];
  const storeUrl = storeUrlParts.every(Boolean) ? storeUrlParts.join('/') : '';

  const items = [
    { name: 'Store Locator', url: 'store-locator' },
    { name: 'Store Directory', url: 'storeDirectory' },
    ...(sanitizedState ? [{ name: state, url: sanitizedState }] : []),
    ...(storeUrl ? [{ name: storeName, url: storeUrl }] : []),
    ...(storeUrl && departmentName && departmentID
      ? [{ name: departmentName, url: `${storeUrl}/${departmentID}` }]
      : [])
  ];

  return generateBreadcrumbList(items, { urlPrefix: HD_URL_L });
};